import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Dialog, Stack } from '@mui/material'
import { ReactComponent as NextIcon } from 'assets/arrow_back_logo.svg'
import { ReactComponent as CloseLogo } from 'assets/cross_close_logo.svg'
import { ReactComponent as TagNumberIcon } from 'assets/icons/tag-icon.svg'
import { morphForWords } from 'components/shared/processes'
import { useUpdateLamenessScore } from 'hook/mutations/useUpdateLamenessScore'
import { ICowsList } from 'models/ICattlesData'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import SelectSmall from 'ui/menu/SelectForm'
import Preloader from 'ui/Preloader'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { LAME_HEADS_SCORE_OPTIONS } from './helper/lameHeadsScoreOptions'
import { isLamenessVideoExpired } from './helper/lamenessVideoMock'

interface LamenessVideoModalProps {
	open: boolean
	cow: ICowsList | null
	onClose: () => void
	onScoreUpdated?: (cowId: number, score: number) => void
	canPrev?: boolean
	canNext?: boolean
	onPrev?: () => void
	onNext?: () => void
	isLoading?: boolean
	changedInSession?: boolean
}

const LamenessVideoModal = ({
	open,
	cow,
	onClose,
	onScoreUpdated,
	canPrev = false,
	canNext = false,
	onPrev,
	onNext,
	isLoading = false,
	changedInSession = false,
}: LamenessVideoModalProps) => {
	const [selectedScore, setSelectedScore] = useState<number | null>(null)
	const [manualScore, setManualScore] = useState<number | null>(null)
	const [editedCowId, setEditedCowId] = useState<number | null>(null)
	const [scoreBeforeEdit, setScoreBeforeEdit] = useState<number | null>(null)
	const [photoFailed, setPhotoFailed] = useState(false)
	const { mutate: updateScore, isPending } = useUpdateLamenessScore()
	const { userData } = useAppSelector(state => state.userReducer)

	useEffect(() => {
		if (!cow) {
			setSelectedScore(null)
			setManualScore(null)
			return
		}

		const savedManualScore = cow.limpness_score_is_false
			? (cow.limpness_score_manual ?? cow.limpness)
			: null

		setManualScore(savedManualScore)
		setSelectedScore(savedManualScore ?? cow.limpness)
	}, [cow])

	useEffect(() => {
		setEditedCowId(null)
		setScoreBeforeEdit(null)
		setPhotoFailed(false)
	}, [cow?.id, cow?.limpness_side_photo_url])

	const formatScore = (score: number) =>
		`${score} ${morphForWords(score, ['балл', 'балла', 'баллов'])}`

	const hasManualChange = Boolean(
		manualScore != null && cow?.limpness_score_is_false,
	)
	const showManualResult = editedCowId === cow?.id && manualScore != null
	// зачёркиваем балл, который был показан до правки (подтверждённый двумя замерами)
	const originalScore = scoreBeforeEdit
	const currentUserName =
		[userData?.firstname, userData?.lastname].filter(Boolean).join(' ') ||
		userData?.username ||
		''
	const editorName = cow?.limpness_score_manual_by || currentUserName
	const showEditor = hasManualChange && Boolean(editorName)
	const editorPosition =
		userData?.position &&
		(changedInSession ||
			editedCowId === cow?.id ||
			editorName === currentUserName ||
			editorName === userData?.username)
			? userData.position
			: undefined
	const navigationDisabled = isLoading || isPending
	const videoExpired = isLamenessVideoExpired(cow?.last_limpness_tracking_dt)
	const photoUrl = photoFailed ? null : cow?.limpness_side_photo_url

	const handleScoreChange = (score: number) => {
		if (!cow || isPending || score === selectedScore) return

		const previousScore = selectedScore
		setSelectedScore(score)

		updateScore(
			{
				cow_id: cow.id,
				limpness_score: score,
				// правку балла привязываем к замеру, видео которого смотрит пользователь
				tracking_id: cow.last_limpness_tracking_id ?? null,
			},
			{
				onSuccess: response => {
					setManualScore(response.limpness)
					setSelectedScore(response.limpness)
					setScoreBeforeEdit(previousScore)
					setEditedCowId(cow.id)
					onScoreUpdated?.(cow.id, response.limpness)
				},
				onError: () => {
					setSelectedScore(previousScore)
				},
			},
		)
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			sx={{
				'& .MuiDialog-container': {
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
					'&::-webkit-scrollbar': {
						display: 'none',
					},
				},
			}}
			PaperProps={{
				sx: {
					borderRadius: '12px',
					width: {
						xs: 'calc(100vw - 32px)',
						sm: 'min(1680px, calc(100vw - 40px))',
					},
					maxHeight: 'calc(100vh - 32px)',
					m: { xs: 2, sm: 0 },
					overflowY: 'auto',
					boxSizing: 'border-box',
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
					'&::-webkit-scrollbar': {
						display: 'none',
					},
				},
			}}
		>
			<Box p={{ xs: '16px', sm: '24px' }}>
				<Box
					display='flex'
					justifyContent='space-between'
					alignItems='center'
					gap='16px'
					mb='16px'
				>
					<Box display='flex' alignItems='center' gap='8px' minWidth={0}>
						<TypographyHeader>Видеофиксация хромоты коровы</TypographyHeader>
						<TagNumberIcon />
						<TypographyHeader>{cow?.tag_id ?? '-'}</TypographyHeader>
					</Box>
					<CloseLogo
						onClick={onClose}
						stroke={COLORS.secondary}
						style={{ cursor: 'pointer', flexShrink: 0 }}
					/>
				</Box>

				<Box display='flex' alignItems='center' gap='8px'>
					<NextIcon
						onClick={canPrev && !navigationDisabled ? onPrev : undefined}
						style={{
							flexShrink: 0,
							opacity: canPrev && !navigationDisabled ? 1 : 0.3,
							cursor: canPrev && !navigationDisabled ? 'pointer' : 'default',
						}}
					/>
					<Box
						sx={{
							height: {
								xs: 220,
								sm: 'min(640px, calc(100vh - 280px))',
							},
							minHeight: { sm: 480 },
							width: '100%',
							minWidth: 0,
							display: 'flex',
						}}
					>
						{isLoading ? (
							<Stack
								alignItems='center'
								justifyContent='center'
								height='100%'
								width='100%'
							>
								<Preloader />
							</Stack>
						) : (
							<>
								<Box
									sx={{
										flex: 1,
										minWidth: 0,
										height: '100%',
										overflow: 'hidden',
									}}
								>
									{videoExpired ? (
										<Stack
											alignItems='center'
											justifyContent='center'
											height='100%'
											px='16px'
											textAlign='center'
											gap='8px'
										>
											<TypographyPrimary
												sx={{
													fontWeight: 600,
													fontSize: '16px',
													lineHeight: '22px',
													color: COLORS.secondary,
												}}
											>
												Архивное видео недоступно.
											</TypographyPrimary>
											<TypographySecondary
												sx={{
													width: '100%',
													maxWidth: '600px',
													fontSize: '14px',
													lineHeight: '20px',
													textAlign: 'center',
												}}
											>
												Этот замер был сделан более 7 дней назад. Видеофрагменты
												хромоты доступны для просмотра в течение 7 дней с
												момента фиксации. Текстовые данные и результаты замера
												по-прежнему сохранены в системе.
											</TypographySecondary>
										</Stack>
									) : cow?.limpness_video_url ? (
										<iframe
											src={cow.limpness_video_url}
											style={{ width: '100%', height: '100%', border: 0 }}
											allowFullScreen
											title='Видеофиксация хромоты'
										/>
									) : (
										<Stack
											alignItems='center'
											justifyContent='center'
											height='100%'
											bgcolor={COLORS.background}
										>
											<TypographySecondary>
												Видео недоступно
											</TypographySecondary>
										</Stack>
									)}
								</Box>
								<Box
									sx={{
										width: '20px',
										flexShrink: 0,
										display: 'flex',
										justifyContent: 'center',
									}}
								>
									<Box
										sx={{
											width: '1px',
											height: '100%',
											bgcolor: COLORS.secondary,
										}}
									/>
								</Box>
								<Box
									sx={{
										flex: 1,
										minWidth: 0,
										height: '100%',
										overflow: 'hidden',
									}}
								>
									{photoUrl ? (
										<img
											src={photoUrl}
											alt='Фото коровы'
											onError={() => setPhotoFailed(true)}
											style={{
												width: '100%',
												height: '100%',
												objectFit: 'contain',
												objectPosition: 'center',
												display: 'block',
											}}
										/>
									) : (
										<Stack
											alignItems='center'
											justifyContent='center'
											height='100%'
										>
											<TypographySecondary
												sx={{
													fontSize: '14px',
													lineHeight: '20px',
													fontWeight: 600,
												}}
											>
												Нет данных
											</TypographySecondary>
										</Stack>
									)}
								</Box>
							</>
						)}
					</Box>
					<NextIcon
						onClick={canNext && !navigationDisabled ? onNext : undefined}
						style={{
							flexShrink: 0,
							transform: 'rotate(180deg)',
							opacity: canNext && !navigationDisabled ? 1 : 0.3,
							cursor: canNext && !navigationDisabled ? 'pointer' : 'default',
						}}
					/>
				</Box>
				{!hasManualChange && (
					<Box display='flex' alignItems='center' gap='8px' mt='16px'>
						<InfoOutlinedIcon sx={{ color: COLORS.secondary, fontSize: 20 }} />
						<TypographySecondary>
							Если балл определен ложно, выберите корректный балл из выпадающего
							списка.
						</TypographySecondary>
					</Box>
				)}

				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', sm: '140px minmax(0, 1fr)' },
						columnGap: '16px',
						rowGap: '12px',
						alignItems: 'center',
						mt: '20px',
					}}
				>
					<TypographySecondary>Дата и время</TypographySecondary>
					<TypographyPrimary>
						{cow?.last_limpness_tracking_dt
							? moment(cow.last_limpness_tracking_dt).format('HH:mm DD.MM.YY')
							: '-'}
					</TypographyPrimary>

					<TypographySecondary
						sx={{ alignSelf: 'start', mt: showManualResult ? 0 : '12px' }}
					>
						Хромота, балл
					</TypographySecondary>
					<Box display='flex' alignItems='center' gap='12px' flexWrap='wrap'>
						{showManualResult && cow ? (
							<>
								{originalScore != null && (
									<TypographyPrimary
										sx={{
											color: COLORS.secondary,
											textDecoration: 'line-through',
										}}
									>
										{formatScore(originalScore)}
									</TypographyPrimary>
								)}
								<TypographyPrimary>
									{formatScore(manualScore)}
								</TypographyPrimary>
							</>
						) : (
							<>
								<Box
									sx={{
										pointerEvents: isPending ? 'none' : 'auto',
										opacity: isPending ? 0.7 : 1,
									}}
								>
									<SelectSmall
										key={`${cow?.id ?? 'empty'}-${selectedScore ?? 'empty'}`}
										typeArray={LAME_HEADS_SCORE_OPTIONS}
										isCheckbox={false}
										isMultiplaySelect={false}
										disableDeselect
										closeOnSelect
										menuPlacement='bottom'
										menuInFlow
										value={selectedScore ?? undefined}
										onClick={handleScoreChange}
										floatingLabel='Хромота'
										placeholder='Балл'
										height='40px'
										width='134px'
									/>
								</Box>
								{isPending && <Preloader size={20} />}
							</>
						)}
					</Box>
					{showEditor && (
						<>
							<TypographySecondary>Внес изменения</TypographySecondary>
							<Stack gap='2px'>
								<TypographyPrimary>{editorName}</TypographyPrimary>
								{editorPosition && (
									<TypographySecondary>{editorPosition}</TypographySecondary>
								)}
							</Stack>
						</>
					)}
				</Box>
			</Box>
		</Dialog>
	)
}

export default LamenessVideoModal
